import { Injectable } from '@angular/core';
import { isNullOrUndef } from 'chart.js/helpers';
import { Circle, Fill, RegularShape, Stroke, Style } from 'ol/style';
import { coverageDTO } from '../models/coverageDTO';
import { ConfigurationConstants, ShapeType } from '../models/EnumTypes';
import { NavaidDTOCalculation, NavaidModel } from '../models/navaid';
import { PerformanceDTO } from '../models/performanceDTO';
import { ProcedureModel } from '../models/procedure';
import { ProcedureLegModel } from '../models/procedure-leg';
import { SelectCoordinatePointsControlService } from '../popup/services/select-coordinate-points-control.service';
import { ConfigurationService } from './configuration.service';
import { v4 as uuidv4 } from 'uuid';
import { GritUserRoleGroup } from './grit/grit.user.service';
import { EndpointsService } from './endpoints.service';

@Injectable({
  providedIn: 'root'
})
export class UtilsService {

  constructor(
    //private selectProcedureService : SelectCoordinatePointsControlService
    public configurationService: ConfigurationService,
    private endpointsService: EndpointsService
    ) { }

  public feetToMeters(feet:any){
    return feet * 0.3048;
  }

  public metersToFeet(meters:any){
    return meters * 3.28084;
  }

  public flToFeet(fl:any){
		return fl *  100;
	}

  public nmToMeters(nm: any){
    return nm * 1852;
  }

  public metersToNm(meters: any){
    return meters * 0.0005399568;
  }

  public arcSecondsToMeters(arcSeconds: number){
		return arcSeconds*30;
	}

  getSum(value1:number, value2:number) : number{
    return value1 + value2;
  }


  validFOM(fom_value:string) : boolean{
    if (fom_value == null || fom_value == '7: NOT INCLUDED' || fom_value == '9: OUT OF SERVICE'){
      return false;
    }
    return true;
  }

  validDOC(doc:number) : boolean{
    if (!this.validPositiveNumber(doc) || doc < 1 || doc > this.configurationService.currentConfiguration.facilityConfiguration.docMaxRange){
      return false;
    }
    return true;
  }

  validPositiveNumber(n:number) : boolean{
    if (n == null || isNaN(n) || n < 0){
      return false;
    }
    return true;
  }

  getDocAndAltitude(value: string): string[] {
    let stringList : string[] = [];
		if(value == null) {
			return stringList;
		}
		var re = /(?<=< |> )(.*?)(?=\[)/g;
    var m;

    do {
        m = re.exec(value);
        if (m) {
            stringList.push(m[1]);
        }
    } while (m);

		 if (stringList.length == 1) {
			 stringList.push("80000");
		 }
		return stringList;
  }

  getArincFomText(value : string) : string {
    switch(value){
      case 'TERMINAL_USE':
        return '0: DOC < 25[NM] - ALTITUDE < 12000[FT]';
        break;
      case 'LOW_ALTITUDE_USE':
        return '1: DOC < 40[NM] - ALTITUDE < 18000[FT]';
        break;
      case 'HIGH_ALTITUDE':
        return '2: DOC < 130[NM] - ALTITUDE < 60000[FT]';
        break;
      case 'EXTENDED_HIGH_ALTITUDE':
        return '3: DOC > 130[NM]';
        break;
      case 'NOT_INCLUDED':
        return '7: NOT INCLUDED';
        break;
      case 'OUT_OF_SERVICE':
        return '9: OUT OF SERVICE';
        break;
    }
    return '';
  }

  base64ToBlob(base64String: string, mimeType: string = "application/zip"): Blob | null {
    if (!base64String || typeof base64String !== 'string') {
      console.error("base64ToBlob: input is undefined or not a string", base64String);
      return null;
    }
    // Remove the data URL prefix if present
    const base64Data = base64String.includes(',') ? base64String.split(',')[1] : base64String;
    try {
      const binaryData = atob(base64Data);
      const arrayBuffer = new ArrayBuffer(binaryData.length);
      const uint8Array = new Uint8Array(arrayBuffer);
      for (let i = 0; i < binaryData.length; i++) {
        uint8Array[i] = binaryData.charCodeAt(i);
      }
      return new Blob([uint8Array], { type: mimeType });
    } catch (error) {
      console.error("Error creating Blob:", error);
      return null;
    }
  }

  getArincFomValue(value : string) : string {
    switch(value){
      case '0: DOC < 25[NM] - ALTITUDE < 12000[FT]':
        return 'TERMINAL_USE';
        break;
      case '1: DOC < 40[NM] - ALTITUDE < 18000[FT]':
        return 'LOW_ALTITUDE_USE';
        break;
      case '2: DOC < 130[NM] - ALTITUDE < 60000[FT]':
        return 'HIGH_ALTITUDE';
        break;
      case '3: DOC > 130[NM]':
        return 'EXTENDED_HIGH_ALTITUDE';
        break;
      case '7: NOT INCLUDED':
        return 'NOT_INCLUDED';
        break;
      case '9: OUT OF SERVICE':
        return 'OUT_OF_SERVICE';
        break;
    }
    if(value == null || value == 'null')
      return '';
    return value;
  }
  getStyleOfShape(shape:string,strokeColor:string,strokeWidth:number,fillColor:string,radius:number): Style{
    const stroke = strokeColor ? new Stroke({ color: strokeColor, width: strokeWidth }) : null;
    const fill = fillColor? new Fill({ color: fillColor }): null;

    let style;

    switch (shape) {
      case ShapeType.Circle:
        style = new Style({
          image: new Circle({
            radius: radius,
            stroke,
            fill,
          }),
        });
        break;
      case ShapeType.Square:
        style = new Style({
          image: new RegularShape({
            points: 4,
            radius: radius,
            angle: Math.PI / 4,
            stroke,
            fill,
          }),
        });
        break;
      case ShapeType.Rectangle:
        style = new Style({
          image: new RegularShape({
            radius: radius / Math.SQRT2,
            radius2: radius,
            points: 4,
            angle: 0,
            stroke,
            fill,
          }),
        });
        break;
      case ShapeType.Triangle:
        style = new Style({
          image: new RegularShape({
            points: 3,
            radius: radius,
            rotation: Math.PI / 4,
            angle: 0,
            stroke,
            fill,
          }),
        });
        break;
      case ShapeType.Star:
        style = new Style({
          image: new RegularShape({
            points: 5,
            radius: radius,
            radius2: radius*5/2,
            angle: 0,
            stroke,
            fill,
          }),
        });
        break;
      case ShapeType.Cross:
        style = new Style({
          image: new RegularShape({
            points: 4,
            radius: radius,
            radius2: 0,
            angle: 0,
            stroke,
            fill,
          }),
        });
        break;
      case ShapeType.X:
        style = new Style({
          image: new RegularShape({
            points: 4,
            radius: radius,
            radius2: 0,
            angle: Math.PI / 4,
            stroke,
            fill,
          }),
        });
        break;
      default:
        // Handle an unknown shape type
        break;
    }

    return style

  }

  getInvalidNavaids(navaids: NavaidModel[], navaidsTerrainHeight: number[]) { // this doesnt take into account the user defined range limitation
    const invalidNavaids = [];
    for (let i = 0; i < navaids.length; i++) {
      if (!this.isValidNavaid(navaids[i], navaidsTerrainHeight[i])) {
        invalidNavaids.push(navaids[i]);
      }
    }

    return invalidNavaids;
  }

  isValidNavaid(navaid: NavaidModel, navaidTerrainHeight: number) { // this doesnt take into account the user defined range limitation
    if (!this.navaidHasRequiredGeneralCalculationsFields(navaid)) {
      return false;
    }

    if (navaidTerrainHeight && !this.validElevation(navaidTerrainHeight - navaid.antheightMeters, navaid.effectiveHeight)) {
      return false;
    }

    return true;
  }

  navaidHasRequiredGeneralCalculationsFields(navaid: NavaidModel) {
    if (!navaid.doc || Number(navaid.doc) < ConfigurationConstants.DOC_MIN || Number(navaid.doc) >= ConfigurationConstants.DOC_MAX) {
      return false;
    }
    if (!navaid.maxaltitude || navaid.maxaltitude > ConfigurationConstants.ALTITUDE_MAX || navaid.maxaltitude < ConfigurationConstants.ALTITUDE_MIN) {
      return false;
    }
    if (this.configurationService.currentConfiguration.calculation.elevationType != "Relative") {
      if ((navaid.elevation == null
        || Number(navaid.elevation) > ConfigurationConstants.ELEVATION_MAX_FT
        || Number(navaid.elevation) < ConfigurationConstants.ELEVATION_MIN_FT)) {

        return false;
      }
    }

    return true;
  }



  defineRangeLimitation(rangeLimitation:String, arinc424fom:string,navaid : NavaidDTOCalculation, terrainElevation: number) : NavaidDTOCalculation{
    if(rangeLimitation == "FOM") {
      if(arinc424fom != null) {
        let values : string[] = this.getDocAndAltitude(arinc424fom);

        if (values.length > 1 && arinc424fom != '7: NOT INCLUDED' && arinc424fom != '9: OUT OF SERVICE') {
          navaid.doc = parseInt(values[0]);
          navaid.maxaltitude = parseInt(values[1]);
        }
        else {
          return new NavaidDTOCalculation();// return empty NavaidDTOCalculation when invalid
        }

      }else {
        return new NavaidDTOCalculation();// return empty NavaidDTOCalculation when invalid
      }
    }
    if (terrainElevation) {
      // terrain elevation includes antHeight. In this case the effective Height is the published height
      if (!this.validElevation(terrainElevation - navaid.antheightMeters, navaid.effectiveHeight)) {
        return new NavaidDTOCalculation();
      }
    }
    if(rangeLimitation == "DOC"){
      if (!this.validDOC(navaid.doc)){
        return new NavaidDTOCalculation();
      }
    }
    if(rangeLimitation == "Radio Horizon" || rangeLimitation == "FSPL"){
      // navaid.doc = 160;
      navaid.doc = this.configurationService.currentConfiguration.facilityConfiguration.docMaxRange;
      // navaid.maxaltitude = 60000;
      navaid.maxaltitude = this.configurationService.currentConfiguration.defaultAltitudes.maxAltitudeFT;
    }

    if (navaid.maxaltitude == null) {
      return new NavaidDTOCalculation();
    }
    
    return navaid;
  }

  validElevation(terrainElevation: number, navaidElevation: number) {
    return !(navaidElevation < terrainElevation
      || navaidElevation - terrainElevation > this.configurationService.currentConfiguration.facilityConfiguration.aboveTerrainLimitM)
  }


  checkDocMaxAltitude(maxAltitude : number, navaid : NavaidDTOCalculation) : boolean{
    if (navaid.maxaltitude/100 >= maxAltitude){
      return true;
    }
    return false;
  }

  checkCalculationAltitude(coverage? : coverageDTO, performance? : PerformanceDTO, type?: string) : NavaidDTOCalculation[]{
    let listOfNavaidsInvalids: NavaidDTOCalculation[] = [];
    
    let dto = null;

    if (coverage) {
      dto = coverage;
    } else if (performance) {
      dto = performance;
    } else {
      return listOfNavaidsInvalids;
    }

    dto.navaids.forEach(nav => {
      if (nav.maxaltitude == null) {
        return; // continue in forEach.  
      }
      if (dto.maxAltitude > nav.maxaltitude / 100) {
        listOfNavaidsInvalids.push(nav);
      }
    });

    return listOfNavaidsInvalids;
  }

  public containsObject(obj, list) : boolean {
    var i;
    for (i = 0; i < list.length; i++) {
        if (list[i] === obj) {
            return true;
        }
    }

    return false;
  }

  public getDynamicTableColumns(entity : string) : String[]{
    switch(entity){
      case 'Navaids':
        return ["isSelected","Id", "Name", "Type", "ICAOCode", "Published", "Latitude", "Longitude"];
      case 'Coordenate Points':
        return ["isSelected","Id", "Name", "Type", "Published", "Latitude", "Longitude"];
      case 'Procedures':
        return ["isSelected","Id", "Name", "Type", "Airport", "MapStatus"];
      case 'AirSpaceBoundaries':
        return ["isSelected", "id", "Name"];
      case 'Trajectories':
        return ["isSelected", "ProcedureName", "ProcedureId", "TrajectoryName", "TrajectoryId", "TotalPoints", "MapStatus"];
      case 'SAAM Traffic':
        return ["isSelected", "id", "Name"];
      case 'Flight Inspection':
        return ["isSelected", "id", "Name"];
      case 'Processing Tool':
        return ["isSelected", "id", "Name"];
      case 'Terrains':
        return ["isSelected", "TerrainName", "TerrainType", "ProjectName"];
      default:
        return [];
    }
  }


  public replacer(key : any, value : any) {
    if(value instanceof Map) {
      return {
        dataType: 'Map',
        value: Array.from(value.entries()), // or with spread: value: [...value]
      };
    } else {
      return value;
    }
  }

  public reviver(key : any, value : any) {
    if(typeof value === 'object' && value !== null) {
      if (value.dataType === 'Map') {
        return new Map(value.value);
      }
    }
    return value;
  }

  public before(date1 : Date, date2 : Date) : boolean {
    return date1 < date2;
  }

  public after(date1 : Date, date2 : Date) : boolean {
    return date1 > date2;
  }

  public equals(date1 : Date, date2 : Date) : boolean {
    return date1.getTime() == date2.getTime();
  }

  showHiddenMenu(evt) {
    this.closeHiddenMenu(evt);

    if(evt.srcElement.classList.contains('dots')) {
      evt.srcElement.nextElementSibling.classList.add('dotsHiddenMenu','active');
    }
    else if (evt.srcElement.classList.contains('create')) {
      evt.srcElement.nextElementSibling.classList.add('createHiddenMenu','active');
    }
  }

  closeHiddenMenu(evt) {
    const elements = document.querySelectorAll('.createHiddenMenu.active, .dotsHiddenMenu.active');
    elements.forEach((element) => {
      element.classList.remove('createHiddenMenu', 'active', 'dotsHiddenMenu');
    });
  }
  hexToRgb(hex: string): RGBColor {
    // Remove the "#" symbol, if it exists
    hex = hex.replace("#", "");

    // Parse the hex string to a decimal number
    const num = parseInt(hex, 16);

    // Extract the red, green, and blue components from the decimal number
    const r = (num >> 16) & 255;
    const g = (num >> 8) & 255;
    const b = num & 255;
    let rgbcolor : RGBColor = new RGBColor();
    rgbcolor.red = r;
    rgbcolor.green = g;
    rgbcolor.blue = b;
    return rgbcolor;
  }
  //to replace the function above
  hexToRgb2(hex:string, transparency:number){
      let [r,g,b] = this.extractHexValuesFromString(hex);
      return `rgba(${r},${g},${b},${transparency})`;
  }

  extractHexValuesFromString(hex:string){
    // Remove the "#" symbol, if it exists
    hex = hex.replace("#", "");

    // Parse the hex string to a decimal number
    const num = parseInt(hex, 16);

    // Extract the red, green, and blue components from the decimal number
    const r = (num >> 16) & 255;
    const g = (num >> 8) & 255;
    const b = num & 255;
    return [r,g,b]
  }
  changeFillTransparency(fill:Fill,transparency:number){
    let fillColor:string =(fill.getColor() as string);
    if(fillColor.startsWith("#")){
      let rgbColor:RGBColor = this.hexToRgb(fillColor);
      fill.setColor(`rgba(${rgbColor.red},${rgbColor.green},${rgbColor.blue},${transparency})`);
    }
    else{
      fill.setColor(fillColor.substring(0,fillColor.lastIndexOf(',') + 1) + transparency + ')');
    }

  }
  changeStrokeTransparency(stroke: Stroke, transparency:number){
    let strokeColor:string =(stroke.getColor() as string);
    if(strokeColor.startsWith("#")){
      let rgbColor:RGBColor = this.hexToRgb(strokeColor);
      stroke.setColor(`rgba(${rgbColor.red},${rgbColor.green},${rgbColor.blue},${transparency})`);
    }
    else{
      stroke.setColor(strokeColor.substring(0,strokeColor.lastIndexOf(',') + 1) + transparency + ')');
    }

  }
  rgbToHex(r: number, g: number, b: number, a?:number): string {
   // Normalize the color components to the range [0, 1]
   const normR = Math.min(Math.max(r / 255, 0), 1);
   const normG = Math.min(Math.max(g / 255, 0), 1);
   const normB = Math.min(Math.max(b / 255, 0), 1);

   // Convert the normalized values to hexadecimal strings
   const hexR = Math.round(normR * 255).toString(16).padStart(2, "0");
   const hexG = Math.round(normG * 255).toString(16).padStart(2, "0");
   const hexB = Math.round(normB * 255).toString(16).padStart(2, "0");

    // Concatenate the hexadecimal strings with "#" symbol to form the hex color code
    return `#${hexR}${hexG}${hexB}`;
  }
  public extractRGBAValues(rgbaString: string): [number, number, number, number] | null {
    const regex = /^rgba\(\s*(\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)$/i;
    const matches = rgbaString.match(regex);

    if (matches) {
      const [, r, g, b, a] = matches.map(parseFloat);
      return [r, g, b, a];
    }

    return null;
  }
  public rgbaStringToHex(rgba:string){
    const [r, g, b, a]  : number[] = this.extractRGBAValues(rgba);
    return this.rgbToHex(r,g,b,a);
  }
  public validateFileName(fname: string): boolean{
    if (fname == null || fname == undefined){
      return false;
    }
    if (fname.length == 0){
      return false;
    }
    const rg1=/^[^\\/:\*\?"<>\|]+$/; // forbidden characters \ / : * ? " < > |
    const rg2=/^\./; // cannot start with dot (.)
    const rg3=/^(nul|prn|con|lpt[0-9]|com[0-9])(\.|$)/i; // forbidden file names
    return rg1.test(fname)&&!rg2.test(fname)&&!rg3.test(fname);
  }

  public static getIsAdminFromJwtToken(jwtToken: string): boolean{ 
    if (!jwtToken) {
      return false;
    }
    const jwtData = jwtToken.split('.')[1]
    const decodedJwtJsonData = window.atob(jwtData)
    const decodedJwtData = JSON.parse(decodedJwtJsonData)
    const roles: string[] = decodedJwtData.ROLES;
    return roles.includes(GritUserRoleGroup.Admin);
  }

  public async userIsAdmin(): Promise<boolean> {
    let jwtToken: string | null = null;
    while (!jwtToken) {
      jwtToken = this.endpointsService.getUserRolesToken();
      if (!jwtToken) {
        await new Promise(resolve => setTimeout(resolve, 100)); // Wait for jwt token to be available
      }
    }
    return UtilsService.getIsAdminFromJwtToken(jwtToken);
  }

  public static generateGuid(): string {
    const newGuid: string = uuidv4();
    return newGuid;
  }

  public static divideFile(file: File, maxSizeInMegaBytes: number): File[] {
    const maxChunkSize = maxSizeInMegaBytes * 1024 * 1024; // conversion to bytes
    const fileSize = file.size;
    const chunks: File[] = [];
    let offset = 0;

    while (offset < fileSize) {
      const chunkSize = Math.min(maxChunkSize, fileSize - offset);
      const chunk = file.slice(offset, offset + chunkSize);
      const chunkFile = new File([chunk], file.name, { type: file.type });
      chunks.push(chunkFile);
      offset += chunkSize;
    }

    return chunks;
  }

  public static convertBase64StringToBlob(base64String: string): Blob {
    const byteCharacters = atob(base64String);
    const byteArrays = [];

    for (let i = 0; i < byteCharacters.length; i++) {
        byteArrays.push(byteCharacters.charCodeAt(i));
    }

    const byteArray = new Uint8Array(byteArrays);
    return new Blob([byteArray]);
  }

  public static convertBase64ToUint8Array(base64String: string): Uint8Array {
    const binaryString = atob(base64String);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; ++i) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
}


}
export class RGBColor{
  red:number;
  green: number;
  blue: number;
}
